import React, {ButtonHTMLAttributes} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {GlobalLocationTypes} from '../../../utils/types';
import cn from 'classnames';

import Icon, {IconNames} from 'components/acommon/Icon';
import styles from './Button.module.sass';

export type ButtonProps = ButtonHTMLAttributes<any> & {
  children?: React.ReactNode;
  label?: string;
  onClick?: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
  className?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  filled?: boolean;
  secondary?: boolean;
  danger?: boolean;
  stroke?: boolean;
  link?: boolean;
  type?: 'submit' | 'button';
  to?: {pathname: string; state?: GlobalLocationTypes};
  leftIcon?: IconNames;
  rightIcon?: IconNames;
};

const defaultProps: ButtonProps = {
  children: '',
  label: '',
  size: 'large',
  stroke: false,
  filled: false,
  disabled: false,
  onClick: () => {},
  className: undefined,
  type: undefined,
};

function Button({
  children,
  label,
  size,
  onClick,
  disabled,
  className,
  filled,
  secondary,
  danger,
  stroke,
  link,
  type,
  to,
  leftIcon,
  rightIcon,
  ...props
}: ButtonProps) {
  const location = useLocation();

  const classNameComposed = cn(className, styles.button, styles[`button_size_${size}`], {
    [styles.button_filled]: filled,
    [styles.button_stroke]: stroke,
    [styles.button_secondary]: secondary,
    [styles.button_danger]: danger,
    [styles.button_link]: link,
  });
  const content = (
    <>
      {leftIcon && <Icon name={leftIcon} className={styles.leftIcon} />}
      <span
        className={cn(styles.content, {
          [styles.contentRightIcon]: rightIcon,
        })}
      >
        {label || children}
      </span>
      {rightIcon && <Icon name={rightIcon} className={styles.rightIcon} />}
    </>
  );

  return to ? (
    <NavLink
      to={{pathname: to.pathname, state: to.state || location.state}}
      className={classNameComposed}
      activeClassName={styles.active}
    >
      {content}
    </NavLink>
  ) : (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classNameComposed}
      type={type}
      {...props}
    >
      {content}
    </button>
  );
}

Button.defaultProps = defaultProps;
export {Button};
